import { faSignOut } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { confirmAction } from '@functions/entity'
import {
  Box, Button, Form, Link, Line, SectionDivider, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'
import type { SidebarModalProps } from '@domain-group/suit-theme'

import { generateRedirectUrl } from '@domain-group/javascript-utils'

import useAgency, { useAgencyForm } from '@hooks/useAgency'
import useBrand from '@hooks/useBrand'

import EntitySelector from '@components/EntitySelector'
import EntitySelectBox from '@components/EntitySelectBox'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateAgencyModal'

type ModalCallbacks = {
  closeModal: () => void,
  createAgency: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateAgencyModal = (props: SidebarModalProps & ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const {
    callbacks: { createAgency: createFn },
    creating,
  } = useAgency()

  const {
    entityState,
    entityState: {
      address_line_1,
      address_postcode,
      address_region,
      address_state,
      brand_id,
      email,
      geo_country_id,
      phone,
      title,
      website,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useAgencyForm({}, { validateOn: 'change' })

  const brandPayload = useBrand({ id: brand_id })
  const { brand, urls } = brandPayload || {}
  const { editBrandUrl } = urls || {}

  const brandRealbaseId = brand?.data?.external_ids?.realbase

  const confirmAgencyCreatePayload = {
    callbacks: {
      actionFn: createFn,
      afterAction: closeModal,
    },
    options: {
      icon: 'warning',
      entityParams: entityState,
      shouldRedirect: true,
    },
    strings: {
      actionString: 'create',
      confirmString: `If this entity needs to be invoiced for any reason DO NOT proceed with creation.
      Billing Entities must be created via Salesforce.`,
      entityName: 'agency',
      toastText: 'Agency Created',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Agency" />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>Billing Entities MUST be created via Salesforce</SidebarNoticeBox.Title>
          <SidebarNoticeBox.Paragraph>
            If this entity needs to be invoiced for any reason DO NOT proceed with creation.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Box flexDirection="column" flexShrink={0}>
          <EntitySelector
            callbacks={{
              selectItem: brand => setEntityState({ brand_id: brand ? brand.id : null }),
            }}
            entityKey="brands"
            errorMessage={errors.brand_id}
            label="Assign to Brand"
            selectedItemId={brand_id}
          />

          {!!brand_id && !brandRealbaseId && (
            <SidebarModal.NoticeBox boxProps={{ marginTop: 'large' }}>
              <SidebarModal.NoticeBox.Title>
                Brand Not Exported
              </SidebarModal.NoticeBox.Title>

              <SidebarModal.NoticeBox.Paragraph>
                The selected brand must be exported to Realbase before a new agency can be created.
                Please export the brand from the tools menu in the edit brand page.
              </SidebarModal.NoticeBox.Paragraph>

              <Link
                textProps={{ fontSize: 'small', marginTop: 'large' }}
                href={`${editBrandUrl}?redirect=${generateRedirectUrl()}`}
              >
                <FontAwesomeIcon icon={faSignOut} /> Edit Brand
              </Link>
            </SidebarModal.NoticeBox>
          )}

          {!!brand_id && !!brandRealbaseId && (
            <>
              <Line />

              <Form>
                <Form.Field errorMessage={errors.title} label="* Agency Title">
                  <input name="title" value={title} type="text" {...handlers} />
                </Form.Field>

                <Form.Field errorMessage={errors.phone} label="* Contact Number" marginTop="large">
                  <input name="phone" value={phone} type="text" {...handlers} />
                </Form.Field>

                <Form.Field errorMessage={errors.email} label="* Email Address" marginTop="large">
                  <input data-validate-field-on="blur" name="email" value={email} type="text" {...handlers} />
                </Form.Field>

                <Form.Field errorMessage={errors.website} label="* Website" marginTop="large">
                  <input name="website" value={website} type="text" {...handlers} />
                </Form.Field>

                <SectionDivider marginTop="large">BUSINESS ADDRESS</SectionDivider>

                <Form.Field errorMessage={errors.address_line_1} label="* Address Line 1" marginTop="large">
                  <input name="address_line_1" value={address_line_1} type="text" {...handlers} />
                </Form.Field>

                <Form.Row marginTop="large">
                  <Form.Field errorMessage={errors.address_region} hideErrorMessage label="* Suburb/Region">
                    <input name="address_region" value={address_region} type="text" {...handlers} />
                  </Form.Field>

                  <Form.Field
                    errorMessage={errors.address_state}
                    hideErrorMessage
                    label="* State"
                    // @ts-expect-error TS2322
                    marginTop={['large', 0]}
                  >
                    <input name="address_state" value={address_state} type="text" {...handlers} />
                  </Form.Field>
                </Form.Row>

                <Form.Row marginTop="large">
                  <Form.Field errorMessage={errors.address_postcode} hideErrorMessage label="* Postcode">
                    <input name="address_postcode" value={address_postcode} type="text" {...handlers} />
                  </Form.Field>

                  <Form.Field
                    errorMessage={errors.geo_country_id}
                    hideErrorMessage
                    label="* Country"
                    // @ts-expect-error TS2322
                    marginTop={['large', 0]}
                  >
                    <EntitySelectBox
                      data-value-type="number"
                      entityKey="geoCountries"
                      // @ts-expect-error TS2322
                      name="geo_country_id"
                      sortDirection="asc"
                      sortKey="title"
                      value={geo_country_id}
                      {...handlers}
                    />
                  </Form.Field>
                </Form.Row>
              </Form>
            </>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => confirmAction(confirmAgencyCreatePayload)}
          size="large"
        >
          {saveEnabled ? 'Create Agency' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateAgencyModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
